export function datetimeToJS(datetime: string, ignoreTimezone = true) {
  // For date-only strings (e.g. "2026-01-15"), anchor to midnight UTC to prevent
  // ±1 day shifts caused by local timezone interpretation.
  const wTz =
    datetime.length === 10 ? `${datetime}T00:00:00Z` : datetime.slice(0, 19);
  const dt = ignoreTimezone ? wTz : datetime;
  const d = new Date(dt);

  return d;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format a date-only value (UTC-anchored) as "MMM d, yyyy" using UTC components.
 * Use this instead of date-fns `format()` for date-only fields like gameDate
 * to avoid ±1 day shifts from local timezone conversion.
 */
export function formatDateUTC(date: Date): string {
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

/**
 * Format a UTC-anchored Date as "YYYY-MM-DD" using UTC components.
 * Use instead of date-fns formatISO({ representation: "date" }) for date-only
 * fields to avoid ±1 day shifts from local timezone conversion.
 */
export function toISODateUTC(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Convert a UTC-anchored Date to a local Date with the same calendar day.
 * E.g. "2026-02-19T00:00:00Z" in UTC-8 → local Date for Feb 19 (not Feb 18).
 * Use when passing a UTC date to UI components that use local date methods
 * (e.g. calendar/date pickers).
 */
export function utcToLocalDate(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Format a datetime string as "MMM d, yyyy", handling both date-only and
 * full datetime inputs correctly. Date-only strings (e.g. "2026-01-15") use
 * UTC methods to avoid ±1 day shifts; full datetimes use local methods.
 */
export function formatDateDisplay(
  datetime: string,
  includeYear = true
): string {
  const date = datetimeToJS(datetime);
  if (datetime.length === 10) {
    const base = `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
    return includeYear ? `${base}, ${date.getUTCFullYear()}` : base;
  }
  const base = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  return includeYear ? `${base}, ${date.getFullYear()}` : base;
}
